import {
  AllowedService,
  BookingStatus,
  CustomerType,
  ErrorCodeCategory,
  TechnicianLevel,
  TechnicianStatus,
} from './entities/type';
import {
  allowedServices,
  basicServices,
  EnumOptions,
} from './entities/interface';

export const lockWidth = (width: number) => {
  return { width, maxWidth: width, minWidth: width };
};

export const getEnumValue = (key: any): string => {
  return EnumOptions[key] || 'Unknown Key';
};

export const formatDateAndTime = (dateString: string | any): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);

  // Define format options for day, month, weekday, hour, minute, and AM/PM
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true, // 12-hour format with AM/PM
  };

  // Format the date using the options
  return new Intl.DateTimeFormat('en-GB', options).format(date);
};

// For date-only values (e.g. a coupon's expiry date) that are stored as
// UTC midnight — formatting with the viewer's local timezone can shift
// the displayed time away from midnight (e.g. 12:00 AM UTC renders as
// 5:30 AM in IST). Format in UTC and drop the time entirely, since a
// date-only field has no meaningful time-of-day to show.
export const formatDateOnly = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

export const formatLeaveDateRange = (
  startDate: string | Date | null,
  endDate: string | Date | null,
): string => {
  if (!startDate || !endDate) return 'N/A';

  const s = new Date(startDate);
  const e = new Date(endDate);

  if (isNaN(s.getTime()) || isNaN(e.getTime())) return 'N/A';

  const format = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  if (s.toDateString() === e.toDateString()) {
    return format.format(s); // same date
  }

  return `${format.format(s)} - ${format.format(e)}`;
};

export const formatPhoneNumber = (phone: string) => {
  if (!phone) return 'N/A';
  const cleaned = phone.slice(0, 10); // take first 10 digits
  return `+91 (${cleaned.slice(0, 5)}) ${cleaned.slice(5)}`;
};

export const formatDate = (
  date: Date | string | null | undefined,
  locale = 'en-IN',
) => {
  const parsedDate = typeof date === 'string' ? new Date(date) : date;

  if (!parsedDate || isNaN(parsedDate.getTime())) {
    return 'N/A';
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(parsedDate);
};

export const isoToDateString = (isoString: string): string => {
  // Ensure the input is a valid ISO date string
  if (!isoString || isNaN(Date.parse(isoString))) {
    throw new Error('Invalid ISO date string');
  }

  const date = new Date(isoString);

  // Get the year, month, and day parts of the date
  const year: number = date.getFullYear();
  const month: string = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day: string = String(date.getDate()).padStart(2, '0');

  // Return the formatted date string
  return `${year}-${month}-${day}`;
};

export const DateDDMMYYYY = (isoString: string): string => {
  // Ensure the input is a valid ISO date string

  if (isoString === null || isoString === undefined) {
    return 'N/A';
  }
  if (!isoString || isNaN(Date.parse(isoString))) {
    throw new Error('Invalid ISO date string');
  }

  const date = new Date(isoString);

  // Get the year, month, and day parts of the date
  const year: number = date.getFullYear();
  const month: string = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day: string = String(date.getDate()).padStart(2, '0');

  // Return the formatted date string
  return `${day}/${month}/${year}`;
};

// "04/01/2025 - First Half" for the request list tables; never throws
export const formatBookingSlot = (
  date?: string | null,
  slot?: string | null,
): string => {
  const parsedDate =
    date && !isNaN(Date.parse(date)) ? new Date(date) : null;

  const datePart = parsedDate
    ? `${String(parsedDate.getDate()).padStart(2, '0')}/${String(
        parsedDate.getMonth() + 1,
      ).padStart(2, '0')}/${parsedDate.getFullYear()}`
    : '';

  const slotPart = slot ? capitalizeWords(slot.replaceAll('_', ' ')) : '';

  if (datePart && slotPart) return `${datePart} - ${slotPart}`;
  return datePart || slotPart || 'N/A';
};

export const TimeHHMM = (isoString: string | Date): string => {
  if (!isoString) {
    return 'N/A';
  }

  const date = new Date(isoString);

  if (isNaN(date.getTime())) {
    return 'N/A';
  }

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${period}`;
};

export const capitalizeWords = (text: string): string => {
  if (typeof text == 'string') {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  } else {
    return 'N/A';
  }
};

export const getServiceType = (serviceName: string): string => {
  const normalizedServiceName = serviceName.toUpperCase() as AllowedService;

  return basicServices.has(normalizedServiceName)
    ? 'BASIC'
    : normalizedServiceName;
};

type ServiceTypeval = (typeof allowedServices)[number];

export const isServiceType = (value: string): value is ServiceTypeval => {
  return allowedServices.includes(value as ServiceTypeval);
};

export interface StatusProps {
  status: BookingStatus;
}
export const getStatusStyle = (status: BookingStatus): React.CSSProperties => {
  switch (status) {
    case 'BOOKED':
      return { color: '#1976D2', fontWeight: 'bold' }; // Blue
    case 'ASSIGNMENT_PENDING':
      return { color: '#FFB300', fontWeight: 'bold' }; // Amber
    case 'ASSIGNED':
      return { color: '#2196F3', fontWeight: 'bold' }; // Light Blue
    case 'TECHNICIAN_ASSIGNED':
      return { color: '#0288D1', fontWeight: 'bold' }; // Darker Blue
    case 'IN_PROGRESS':
      return { color: '#FF9800', fontWeight: 'bold' }; // Orange
    case 'PAYMENT_PENDING':
      return { color: '#FF5722', fontWeight: 'bold' }; // Deep Orange
    case 'PAID':
      return { color: '#4CAF50', fontWeight: 'bold' }; // Green
    case 'COMPLETE':
      return { color: '#8BC34A', fontWeight: 'bold' }; // Light Green
    case 'DONE':
      return { color: '#9E9E9E', fontWeight: 'bold' }; // Grey
    case 'CANCELLED':
      return { color: '#F44336', fontWeight: 'bold' }; // Red
    default:
      return { color: '#000000', fontWeight: 'bold' }; // Default: Black
  }
};

// Debounce function implementation with explicit type for the function signature
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

export const getStatusLabel = (
  status: BookingStatus | TechnicianLevel | ErrorCodeCategory | CustomerType,
) => {
  const statusLabels = {
    BOOKED: 'Booked',
    ASSIGNMENT_PENDING: 'Assignment Pending',
    TECHNICIAN_ASSIGNED: 'Assigned',
    PAYMENT_PENDING: 'Payment Pending',
    PAID: 'Paid',
    COMPLETE: 'Complete',
    ASSIGNED: 'Assigned',
    IN_PROGRESS: 'In Progress',
    DONE: 'Done',
    CANCELLED: 'Cancelled',
    TRAINEE: 'Trainee',
    JUNIOR: 'Junior',
    SENIOR: 'Senior',
    LEAD: 'Lead',
    CERTIFIED_SPECIALIST: 'Certified Specialist',
    INVERTOR: 'Invertor',
    NON_INVERTOR: 'Non Invertor',
    RETAIL: 'Retail',
    HNI: 'Hni',
    SME: 'Sme',
    LARGE_SCALE: 'Large Scale',
    OEM: 'Oem',
    RESCHEDULED: 'Rescheduled',
    JOB_PENDING: 'Job Pending',
  };

  return statusLabels[status] || 'N/A';
};

export const getStatusColor = (status: TechnicianStatus) => {
  switch (status) {
    case 'AVAILABLE':
      return 'success';
    case 'ON_JOB':
      return 'primary';
    case 'ON_LEAVE':
    case 'ON_BREAK':
      return 'warning';
    case 'ON_TRAINING':
      return 'info';
    case 'DISABLED':
    case 'TERMINATED':
    case 'RESIGNED':
      return 'error';
    default:
      return 'default';
  }
};

export const truncateText = (
  text: string | undefined | null,
  maxLength = 60, // no explicit :number here
): string => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};
